// WbWeighbridgeDetailPage — /weighbridge/settings/weighbridges/:enc_id
// and /new.
//
// Phase C2 Group 1. Tabbed form with four sections:
//   - General        name / code / yard / trade-approved / status
//   - Indicator      protocol + transport-specific config (serial / tcp / usb)
//   - Stability      capacity + threshold + tolerance + duration
//   - Calibration    embedded WbCalibrationsSubpage
//
// `yard_id` is required on create and immutable on update — the field is
// disabled when editing.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { Tabs, type TabItem } from '../../../components/ui/Tabs/Tabs';
import { ConfirmModal } from '../../../components/ui/ConfirmModal/ConfirmModal';
import {
  useCreateWeighbridge,
  useDeleteWeighbridge,
  useUpdateWeighbridge,
  useWbWeighbridge,
  type CreateWeighbridgeInput,
  type UpdateWeighbridgeInput,
} from '../hooks/useWbWeighbridges';
import { useWbYards } from '../hooks/useWbYards';
import { WbCalibrationsSubpage } from '../components/WbCalibrationsSubpage';
import { useWbCameraConfig, useUpdateWbCameraConfig, useAnprLog } from '../hooks/useWbCameraConfig';
import { extractErrorMessage } from '../../../utils/errorMessage';
import type { WbCameraConfigUpdate } from '../../../api/wbCameraConfig';
import styles from './WbWeighbridgeDetailPage.module.css';

type Transport = 'serial' | 'tcp' | 'usb_serial';
type Parity = 'N' | 'E' | 'O';

interface FormState {
  yard_id: string;
  name: string;
  code: string;
  is_trade_approved: boolean;
  status: 'online' | 'offline' | 'maintenance';
  // Indicator — protocol/baud/parity/data/stop-bits are indicator-model
  // facts, owned by this server-side form. `com_port` is NOT: it's which
  // physical USB/serial port the agent machine has the indicator plugged
  // into, which nobody in a browser can know — that's set locally on the
  // agent (Settings → this weighbridge → Serial port) and is intentionally
  // not collected here. See docs/S63_weighbridge_pipeline_findings.md.
  indicator_protocol: string;
  transport: Transport;
  baud_rate: number;
  data_bits: 7 | 8;
  parity: Parity;
  stop_bits: 1 | 2;
  tcp_host: string;
  tcp_port: number;
  // Stability & Capacity
  max_capacity_kg: string;
  min_threshold_kg: string;
  stable_tolerance_kg: string;
  stable_duration_ms: string;
}

const EMPTY_FORM: FormState = {
  yard_id: '',
  name: '',
  code: '',
  is_trade_approved: false,
  status: 'offline',
  indicator_protocol: 'generic_continuous_ascii',
  transport: 'serial',
  baud_rate: 9600,
  data_bits: 8,
  parity: 'N',
  stop_bits: 1,
  tcp_host: '',
  tcp_port: 5000,
  max_capacity_kg: '60000',
  min_threshold_kg: '0',
  stable_tolerance_kg: '5',
  stable_duration_ms: '2000',
};

// Only protocols the agent actually has a driver for (see apps/agent's
// DriverRegistry: mettler-toledo, dini-argeo, generic-ascii). Avery/Rinstrum/
// Cardinal were previously listed here with no corresponding agent driver —
// selecting one would silently fail once/if this config is ever wired up
// (S63 Part D). Don't offer a choice the agent can't run.
const PROTOCOLS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'generic_continuous_ascii', label: 'Generic (Continuous ASCII)' },
  { value: 'generic_poll', label: 'Generic (Poll Mode)' },
  { value: 'mettler', label: 'Mettler Toledo' },
  { value: 'dini_argeo', label: 'Dini Argeo' },
];

const BAUD_RATES: ReadonlyArray<number> = [
  1200, 2400, 4800, 9600, 19200, 38400, 57600, 115200,
];

const TABS: ReadonlyArray<TabItem> = [
  { id: 'general', label: 'General' },
  { id: 'indicator', label: 'Indicator Config' },
  { id: 'stability', label: 'Stability & Capacity' },
  { id: 'calibration', label: 'Calibration' },
  { id: 'anpr', label: 'ANPR' },
];

export function WbWeighbridgeDetailPage(): JSX.Element {
  const navigate = useNavigate();
  const { enc_id } = useParams<{ enc_id: string }>();
  const isNew = enc_id === 'new' || enc_id === undefined;

  const wbQuery = useWbWeighbridge(isNew ? undefined : enc_id);
  const yardsQuery = useWbYards();
  const createMut = useCreateWeighbridge();
  const updateMut = useUpdateWeighbridge(isNew ? '' : (enc_id as string));
  const deleteMut = useDeleteWeighbridge(isNew ? '' : (enc_id as string));

  const [tab, setTab] = useState<string>('general');
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  // True once the admin has changed something on the Indicator Config tab
  // this session — see hasUserEnteredIndicatorConfig().
  const [indicatorTouched, setIndicatorTouched] = useState(false);

  /** Wraps a FormState updater so any edit on the Indicator tab also marks
   *  it dirty. Keeps every indicator field's onChange one-liner instead of
   *  repeating `setIndicatorTouched(true)` at each call site. */
  function setIndicatorField(update: (p: FormState) => FormState): void {
    setIndicatorTouched(true);
    setForm(update);
  }

  // ANPR tab state
  const cameraConfigQuery = useWbCameraConfig(isNew ? '' : (enc_id as string));
  const updateCameraConfig = useUpdateWbCameraConfig(isNew ? '' : (enc_id as string));
  const anprLogQuery = useAnprLog(isNew ? '' : (enc_id as string));
  const [anprForm, setAnprForm] = useState({
    anpr_mode: '' as 'http_snapshot' | 'rtsp' | '',
    anpr_endpoint: '',
    anpr_username: '',
    anpr_password: '',
    anpr_min_confidence: '80',
    anpr_weight_threshold_kg: '',
  });
  const [anprErrors, setAnprErrors] = useState<Record<string, string>>({});
  const [anprSaveError, setAnprSaveError] = useState<string | null>(null);

  // Hydrate on load. Indicator config is NEVER returned by the server
  // (encrypted server-side); the user must re-enter values to change it.
  useEffect(() => {
    if (isNew) return;
    const wb = wbQuery.data?.weighbridge;
    if (!wb) return;
    setForm((prev) => ({
      ...prev,
      yard_id: wb.yard.enc_id,
      name: wb.name,
      code: wb.code,
      is_trade_approved: wb.is_trade_approved,
      status: wb.status,
      indicator_protocol: wb.indicator_protocol ?? prev.indicator_protocol,
      max_capacity_kg:
        wb.max_capacity_kg !== null ? String(wb.max_capacity_kg) : prev.max_capacity_kg,
      min_threshold_kg:
        wb.min_threshold_kg !== null ? String(wb.min_threshold_kg) : prev.min_threshold_kg,
      stable_tolerance_kg: String(wb.stable_tolerance_kg),
      stable_duration_ms: String(wb.stable_duration_ms),
    }));
  }, [isNew, wbQuery.data]);

  // Default yard once the yards list lands (create mode).
  useEffect(() => {
    if (!isNew) return;
    if (form.yard_id) return;
    const first = yardsQuery.data?.yards?.[0];
    if (first) setForm((p) => ({ ...p, yard_id: first.enc_id }));
  }, [isNew, yardsQuery.data, form.yard_id]);

  // Populate ANPR form when camera config data loads.
  useEffect(() => {
    if (cameraConfigQuery.data) {
      setAnprForm((p) => ({
        ...p,
        anpr_mode: cameraConfigQuery.data.anpr_mode ?? '',
        anpr_endpoint: cameraConfigQuery.data.anpr_endpoint ?? '',
        anpr_username: cameraConfigQuery.data.anpr_username ?? '',
        anpr_password: '', // never pre-fill password
        anpr_min_confidence: String(cameraConfigQuery.data.anpr_min_confidence ?? 80),
        anpr_weight_threshold_kg:
          cameraConfigQuery.data.anpr_weight_threshold_kg !== null
            ? String(cameraConfigQuery.data.anpr_weight_threshold_kg)
            : '',
      }));
    }
  }, [cameraConfigQuery.data]);

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs['name'] = 'Name is required.';
    if (!form.code.trim()) errs['code'] = 'Code is required.';
    if (form.code.length > 20) errs['code'] = 'Code must be 20 characters or fewer.';
    if (isNew && !form.yard_id) errs['yard_id'] = 'Yard is required.';
    const maxC = Number(form.max_capacity_kg);
    if (!Number.isFinite(maxC) || maxC < 1)
      errs['max_capacity_kg'] = 'Capacity must be at least 1 kg.';
    const minT = Number(form.min_threshold_kg);
    if (!Number.isFinite(minT) || minT < 0)
      errs['min_threshold_kg'] = 'Threshold must be 0 or more.';
    const tol = Number(form.stable_tolerance_kg);
    if (!Number.isFinite(tol) || tol < 0.1)
      errs['stable_tolerance_kg'] = 'Tolerance must be at least 0.1 kg.';
    const dur = Number(form.stable_duration_ms);
    if (!Number.isFinite(dur) || dur < 100 || !Number.isInteger(dur))
      errs['stable_duration_ms'] = 'Duration must be ≥ 100 ms (integer).';
    if (form.transport === 'tcp') {
      if (!form.tcp_host.trim()) errs['tcp_host'] = 'TCP host is required.';
      if (form.tcp_port < 1 || form.tcp_port > 65535)
        errs['tcp_port'] = 'Port must be 1-65535.';
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function buildIndicatorConfigJson(): string {
    // com_port is deliberately never included: it's a physical-machine fact
    // (which USB/serial port the agent's host is plugged into) that this
    // server-side form cannot know and does not collect — see FormState.
    const base: Record<string, unknown> = { transport: form.transport };
    if (form.transport === 'serial' || form.transport === 'usb_serial') {
      base['baud_rate'] = form.baud_rate;
      base['data_bits'] = form.data_bits;
      base['parity'] = form.parity;
      base['stop_bits'] = form.stop_bits;
    } else if (form.transport === 'tcp') {
      base['tcp_host'] = form.tcp_host;
      base['tcp_port'] = form.tcp_port;
    }
    return JSON.stringify(base);
  }

  function hasUserEnteredIndicatorConfig(): boolean {
    // Only send indicator_config_json when the user actually edited this tab
    // this session — otherwise, since none of these fields are hydrated back
    // from the server (they're encrypted at rest), saving the General or
    // Stability tab would silently overwrite a previously-saved indicator
    // config with the form's untouched defaults.
    return indicatorTouched;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!validate()) return;

    if (isNew) {
      const body: CreateWeighbridgeInput = {
        yard_id: form.yard_id,
        name: form.name.trim(),
        code: form.code.trim(),
        is_trade_approved: form.is_trade_approved,
        status: form.status,
        indicator_protocol: form.indicator_protocol,
        max_capacity_kg: Number(form.max_capacity_kg),
        min_threshold_kg: Number(form.min_threshold_kg),
        stable_tolerance_kg: Number(form.stable_tolerance_kg),
        stable_duration_ms: Math.trunc(Number(form.stable_duration_ms)),
      };
      if (hasUserEnteredIndicatorConfig()) {
        body.indicator_config_json = buildIndicatorConfigJson();
      }
      createMut.mutate(body, {
        onSuccess: () => navigate('/weighbridge/settings/weighbridges'),
        onError: (err) => setError(extractErrorMessage(err)),
      });
      return;
    }

    // Edit. yard_id is immutable server-side — do NOT include it.
    const body: UpdateWeighbridgeInput = {
      name: form.name.trim(),
      code: form.code.trim(),
      is_trade_approved: form.is_trade_approved,
      status: form.status,
      indicator_protocol: form.indicator_protocol,
      max_capacity_kg: Number(form.max_capacity_kg),
      min_threshold_kg: Number(form.min_threshold_kg),
      stable_tolerance_kg: Number(form.stable_tolerance_kg),
      stable_duration_ms: Math.trunc(Number(form.stable_duration_ms)),
    };
    if (hasUserEnteredIndicatorConfig()) {
      body.indicator_config_json = buildIndicatorConfigJson();
    }
    updateMut.mutate(body, {
      onSuccess: () => navigate('/weighbridge/settings/weighbridges'),
      onError: (err) => setError(extractErrorMessage(err)),
    });
  }

  async function handleAnprSubmit(e: React.FormEvent) {
    e.preventDefault();
    setAnprErrors({});
    setAnprSaveError(null);

    const errors: Record<string, string> = {};

    const thresholdRaw = anprForm.anpr_weight_threshold_kg.trim();
    const thresholdValue = thresholdRaw === '' ? null : Number(thresholdRaw);
    if (thresholdValue !== null && (isNaN(thresholdValue) || thresholdValue <= 0)) {
      errors['anpr_weight_threshold_kg'] = 'Must be a positive number or left blank';
    }

    const confidenceValue = Number(anprForm.anpr_min_confidence);
    if (isNaN(confidenceValue) || confidenceValue < 0 || confidenceValue > 100) {
      errors['anpr_min_confidence'] = 'Must be between 0 and 100';
    }

    if (Object.keys(errors).length > 0) {
      setAnprErrors(errors);
      return;
    }

    const payload: WbCameraConfigUpdate = {
      anpr_mode: (anprForm.anpr_mode as 'http_snapshot' | 'rtsp') || null,
      anpr_endpoint: anprForm.anpr_endpoint.trim() || null,
      anpr_username: anprForm.anpr_username.trim() || null,
      anpr_min_confidence: confidenceValue,
      anpr_weight_threshold_kg: thresholdValue,
    };

    if (anprForm.anpr_password.trim()) {
      payload.anpr_password = anprForm.anpr_password.trim();
    }

    try {
      await updateCameraConfig.mutateAsync(payload);
    } catch (err) {
      setAnprSaveError(extractErrorMessage(err));
    }
  }

  function handleDeleteConfirm() {
    deleteMut.mutate(undefined, {
      onSuccess: () => {
        setConfirmOpen(false);
        navigate('/weighbridge/settings/weighbridges');
      },
      onError: (err) => {
        setConfirmOpen(false);
        setError(extractErrorMessage(err));
      },
    });
  }

  const isSaving = createMut.isPending || updateMut.isPending;
  const isLoadingExisting = !isNew && wbQuery.isLoading;
  const yards = useMemo(() => yardsQuery.data?.yards ?? [], [yardsQuery.data]);

  return (
    <div className={styles.page}>
      <PageHeader
        title={isNew ? 'New Weighbridge' : form.name || 'Edit Weighbridge'}
        subtitle={isNew ? 'Create a new weighbridge' : 'Edit weighbridge configuration'}
        actions={
          <Button
            variant="secondary"
            icon={ArrowLeft}
            onClick={() => navigate('/weighbridge/settings/weighbridges')}
          >
            Back to Weighbridges
          </Button>
        }
      />

      {isLoadingExisting ? (
        <Card>
          <div className={styles.loading}>
            <Spinner label="Loading weighbridge" />
          </div>
        </Card>
      ) : (
        <>
          <Tabs
            items={TABS as TabItem[]}
            value={tab}
            onChange={setTab}
            className={styles.tabs}
            ariaLabel="Weighbridge settings tabs"
          />
          {tab === 'anpr' ? (
            isNew ? (
              <Card>
                <p>Save weighbridge first to configure ANPR settings.</p>
              </Card>
            ) : (
              <>
              <form onSubmit={handleAnprSubmit} className={styles.form}>
                <Card>
                  <p className={styles.sectionHeading}>Camera Connection</p>
                  <div className={styles.grid}>
                    <label className={styles.field}>
                      <span>Mode</span>
                      <select
                        value={anprForm.anpr_mode}
                        onChange={(e) =>
                          setAnprForm((p) => ({
                            ...p,
                            anpr_mode: e.target.value as 'http_snapshot' | 'rtsp' | '',
                          }))
                        }
                      >
                        <option value="">— not configured —</option>
                        <option value="http_snapshot">HTTP Snapshot</option>
                        <option value="rtsp">RTSP</option>
                      </select>
                    </label>

                    <label className={styles.field}>
                      <span>Endpoint URL</span>
                      <input
                        type="text"
                        value={anprForm.anpr_endpoint}
                        onChange={(e) =>
                          setAnprForm((p) => ({ ...p, anpr_endpoint: e.target.value }))
                        }
                        placeholder="http://192.168.1.x/snapshot or rtsp://..."
                      />
                    </label>

                    <label className={styles.field}>
                      <span>Username</span>
                      <input
                        type="text"
                        value={anprForm.anpr_username}
                        onChange={(e) =>
                          setAnprForm((p) => ({ ...p, anpr_username: e.target.value }))
                        }
                        placeholder="Optional"
                      />
                    </label>

                    <label className={styles.field}>
                      <span>Password</span>
                      <input
                        type="password"
                        value={anprForm.anpr_password}
                        onChange={(e) =>
                          setAnprForm((p) => ({ ...p, anpr_password: e.target.value }))
                        }
                        placeholder={
                          cameraConfigQuery.data?.anpr_has_password
                            ? 'Leave blank to keep current password'
                            : 'Optional'
                        }
                        autoComplete="new-password"
                      />
                    </label>
                  </div>

                  <p className={styles.sectionHeading}>Recognition Settings</p>
                  <div className={styles.grid}>
                    <label className={styles.field}>
                      <span>Minimum confidence (%)</span>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        value={anprForm.anpr_min_confidence}
                        onChange={(e) =>
                          setAnprForm((p) => ({ ...p, anpr_min_confidence: e.target.value }))
                        }
                      />
                      {anprErrors['anpr_min_confidence'] ? (
                        <span className={styles.fieldError}>
                          {anprErrors['anpr_min_confidence']}
                        </span>
                      ) : null}
                    </label>

                    <label className={styles.field}>
                      <span>Auto-trigger threshold (kg)</span>
                      <input
                        type="number"
                        min={0}
                        step={0.001}
                        placeholder="e.g. 500 — leave blank to disable"
                        value={anprForm.anpr_weight_threshold_kg}
                        onChange={(e) =>
                          setAnprForm((p) => ({
                            ...p,
                            anpr_weight_threshold_kg: e.target.value,
                          }))
                        }
                      />
                      {anprErrors['anpr_weight_threshold_kg'] ? (
                        <span className={styles.fieldError}>
                          {anprErrors['anpr_weight_threshold_kg']}
                        </span>
                      ) : null}
                    </label>
                  </div>
                </Card>

                {anprSaveError ? <p className={styles.error}>{anprSaveError}</p> : null}

                <div className={styles.actions}>
                  <div />
                  <div className={styles.rightActions}>
                    <Button type="submit" variant="primary" loading={updateCameraConfig.isPending}>
                      Save
                    </Button>
                  </div>
                </div>
              </form>

              <Card>
                <p style={{ fontWeight: 500, marginBottom: '0.75rem' }}>Recent Recognitions</p>
                {anprLogQuery.isLoading ? (
                  <p className={styles.hint}>Loading…</p>
                ) : !anprLogQuery.data?.entries.length ? (
                  <p className={styles.hint}>No recognition events recorded yet.</p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                    <thead>
                      <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--color-input-border)' }}>
                        <th style={{ padding: '0.4rem 0.5rem' }}>Time</th>
                        <th style={{ padding: '0.4rem 0.5rem' }}>Plate</th>
                        <th style={{ padding: '0.4rem 0.5rem' }}>Confidence</th>
                        <th style={{ padding: '0.4rem 0.5rem' }}>Vehicle</th>
                        <th style={{ padding: '0.4rem 0.5rem' }}>Trigger</th>
                      </tr>
                    </thead>
                    <tbody>
                      {anprLogQuery.data.entries.map((entry) => (
                        <tr key={entry.id} style={{ borderBottom: '1px solid var(--color-input-border)' }}>
                          <td style={{ padding: '0.4rem 0.5rem', color: 'var(--color-text-muted)' }}>
                            {new Date(entry.created_at).toLocaleString()}
                          </td>
                          <td style={{ padding: '0.4rem 0.5rem' }}>
                            {entry.plate_read || '—'}
                          </td>
                          <td style={{ padding: '0.4rem 0.5rem' }}>
                            {entry.plate_read
                              ? `${(Number(entry.confidence) * 100).toFixed(1)}%`
                              : '—'}
                          </td>
                          <td style={{ padding: '0.4rem 0.5rem' }}>
                            {entry.vehicle_registration
                              ? entry.vehicle_registration
                              : entry.plate_read
                              ? 'Not found'
                              : '—'}
                          </td>
                          <td style={{ padding: '0.4rem 0.5rem' }}>
                            {entry.triggered_by === 'weight_threshold' ? 'Auto' : 'Manual'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Card>
              </>
            )
          ) : null}
          {tab !== 'anpr' ? (
          <form onSubmit={handleSubmit} className={styles.form}>
            {tab === 'general' ? (
              <Card>
                <div className={styles.grid}>
                  <label className={styles.field}>
                    <span>Name</span>
                    <input
                      type="text"
                      required
                      maxLength={200}
                      value={form.name}
                      onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                    />
                    {fieldErrors['name'] ? (
                      <span className={styles.fieldError}>{fieldErrors['name']}</span>
                    ) : null}
                  </label>

                  <label className={styles.field}>
                    <span>Code</span>
                    <input
                      type="text"
                      required
                      maxLength={20}
                      value={form.code}
                      onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))}
                      onBlur={(e) =>
                        setForm((p) => ({ ...p, code: e.target.value.toUpperCase() }))
                      }
                    />
                    {fieldErrors['code'] ? (
                      <span className={styles.fieldError}>{fieldErrors['code']}</span>
                    ) : null}
                  </label>

                  <label
                    className={`${styles.field} ${!isNew ? styles.disabled : ''}`}
                  >
                    <span>Yard {isNew ? '' : '(immutable)'}</span>
                    <select
                      required
                      value={form.yard_id}
                      disabled={!isNew}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, yard_id: e.target.value }))
                      }
                    >
                      <option value="">— Select yard —</option>
                      {yards.map((y) => (
                        <option key={y.enc_id} value={y.enc_id}>
                          {y.name} ({y.code})
                        </option>
                      ))}
                    </select>
                    {fieldErrors['yard_id'] ? (
                      <span className={styles.fieldError}>{fieldErrors['yard_id']}</span>
                    ) : null}
                  </label>

                  <label className={styles.field}>
                    <span>Status</span>
                    <select
                      value={form.status}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          status: e.target.value as FormState['status'],
                        }))
                      }
                    >
                      <option value="online">Online</option>
                      <option value="offline">Offline</option>
                      <option value="maintenance">Maintenance</option>
                    </select>
                  </label>

                  <label className={styles.toggleRow}>
                    <input
                      type="checkbox"
                      checked={form.is_trade_approved}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, is_trade_approved: e.target.checked }))
                      }
                    />
                    <span>Trade Approved</span>
                  </label>
                </div>
              </Card>
            ) : null}

            {tab === 'indicator' ? (
              <Card>
                <p className={styles.hint}>
                  ⚠ Not yet applied by the agent. The agent's own local Settings
                  panel (Driver / Serial port / Baud, on the machine running the
                  agent) is what actually controls the live connection today —
                  saving this tab has no effect on a running agent yet. See
                  docs/S63_weighbridge_pipeline_findings.md.
                </p>
                {!isNew ? (
                  <p className={styles.hint}>
                    Indicator config is encrypted and cannot be read back.
                    Re-enter values to update.
                  </p>
                ) : null}
                <div className={styles.grid}>
                  <label className={styles.field}>
                    <span>Protocol</span>
                    <select
                      value={form.indicator_protocol}
                      onChange={(e) =>
                        setIndicatorField((p) => ({ ...p, indicator_protocol: e.target.value }))
                      }
                    >
                      {PROTOCOLS.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className={styles.field}>
                    <span>Transport</span>
                    <select
                      value={form.transport}
                      onChange={(e) =>
                        setIndicatorField((p) => ({
                          ...p,
                          transport: e.target.value as Transport,
                        }))
                      }
                    >
                      <option value="serial">Serial</option>
                      <option value="tcp">TCP</option>
                      <option value="usb_serial">USB Serial</option>
                    </select>
                  </label>
                </div>

                {form.transport === 'serial' || form.transport === 'usb_serial' ? (
                  <div className={styles.grid} style={{ marginTop: '1rem' }}>
                    <p className={styles.hint}>
                      COM port is not set here — it's which physical port the
                      agent's own machine has the indicator plugged into, which
                      this form can't know. Set it on the agent
                      (Settings → this weighbridge → Serial port).
                    </p>
                    <label className={styles.field}>
                      <span>Baud Rate</span>
                      <select
                        value={String(form.baud_rate)}
                        onChange={(e) =>
                          setIndicatorField((p) => ({ ...p, baud_rate: Number(e.target.value) }))
                        }
                      >
                        {BAUD_RATES.map((b) => (
                          <option key={b} value={String(b)}>
                            {b}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className={styles.field}>
                      <span>Data Bits</span>
                      <select
                        value={String(form.data_bits)}
                        onChange={(e) =>
                          setIndicatorField((p) => ({
                            ...p,
                            data_bits: Number(e.target.value) as 7 | 8,
                          }))
                        }
                      >
                        <option value="7">7</option>
                        <option value="8">8</option>
                      </select>
                    </label>
                    <label className={styles.field}>
                      <span>Parity</span>
                      <select
                        value={form.parity}
                        onChange={(e) =>
                          setIndicatorField((p) => ({ ...p, parity: e.target.value as Parity }))
                        }
                      >
                        <option value="N">None</option>
                        <option value="E">Even</option>
                        <option value="O">Odd</option>
                      </select>
                    </label>
                    <label className={styles.field}>
                      <span>Stop Bits</span>
                      <select
                        value={String(form.stop_bits)}
                        onChange={(e) =>
                          setIndicatorField((p) => ({
                            ...p,
                            stop_bits: Number(e.target.value) as 1 | 2,
                          }))
                        }
                      >
                        <option value="1">1</option>
                        <option value="2">2</option>
                      </select>
                    </label>
                  </div>
                ) : (
                  <div className={styles.grid} style={{ marginTop: '1rem' }}>
                    <label className={styles.field}>
                      <span>TCP Host</span>
                      <input
                        type="text"
                        placeholder="e.g. 192.168.1.50"
                        value={form.tcp_host}
                        onChange={(e) =>
                          setIndicatorField((p) => ({ ...p, tcp_host: e.target.value }))
                        }
                      />
                      {fieldErrors['tcp_host'] ? (
                        <span className={styles.fieldError}>
                          {fieldErrors['tcp_host']}
                        </span>
                      ) : null}
                    </label>
                    <label className={styles.field}>
                      <span>TCP Port</span>
                      <input
                        type="number"
                        min={1}
                        max={65535}
                        value={form.tcp_port}
                        onChange={(e) =>
                          setIndicatorField((p) => ({ ...p, tcp_port: Number(e.target.value) }))
                        }
                      />
                      {fieldErrors['tcp_port'] ? (
                        <span className={styles.fieldError}>
                          {fieldErrors['tcp_port']}
                        </span>
                      ) : null}
                    </label>
                  </div>
                )}
              </Card>
            ) : null}

            {tab === 'stability' ? (
              <Card>
                <div className={styles.grid}>
                  <label className={styles.field}>
                    <span>Max Capacity (kg)</span>
                    <input
                      type="number"
                      min={1}
                      required
                      value={form.max_capacity_kg}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, max_capacity_kg: e.target.value }))
                      }
                    />
                    {fieldErrors['max_capacity_kg'] ? (
                      <span className={styles.fieldError}>
                        {fieldErrors['max_capacity_kg']}
                      </span>
                    ) : null}
                  </label>
                  <label className={styles.field}>
                    <span>Min Threshold (kg)</span>
                    <input
                      type="number"
                      min={0}
                      required
                      value={form.min_threshold_kg}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, min_threshold_kg: e.target.value }))
                      }
                    />
                    {fieldErrors['min_threshold_kg'] ? (
                      <span className={styles.fieldError}>
                        {fieldErrors['min_threshold_kg']}
                      </span>
                    ) : null}
                  </label>
                  <label className={styles.field}>
                    <span>Stable Tolerance (kg)</span>
                    <input
                      type="number"
                      min={0.1}
                      step={0.1}
                      required
                      value={form.stable_tolerance_kg}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, stable_tolerance_kg: e.target.value }))
                      }
                    />
                    {fieldErrors['stable_tolerance_kg'] ? (
                      <span className={styles.fieldError}>
                        {fieldErrors['stable_tolerance_kg']}
                      </span>
                    ) : null}
                  </label>
                  <label className={styles.field}>
                    <span>Stable Duration (ms)</span>
                    <input
                      type="number"
                      min={100}
                      step={100}
                      required
                      value={form.stable_duration_ms}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, stable_duration_ms: e.target.value }))
                      }
                    />
                    {fieldErrors['stable_duration_ms'] ? (
                      <span className={styles.fieldError}>
                        {fieldErrors['stable_duration_ms']}
                      </span>
                    ) : null}
                  </label>
                </div>
              </Card>
            ) : null}

            {tab === 'calibration' ? (
              isNew ? (
                <Card>
                  <p>Save weighbridge first to manage calibrations.</p>
                </Card>
              ) : (
                <WbCalibrationsSubpage weighbridgeEncId={enc_id as string} />
              )
            ) : null}

            {error ? <p className={styles.error}>{error}</p> : null}

            {tab !== 'calibration' || isNew ? (
              <div className={styles.actions}>
                <div>
                  {!isNew ? (
                    <Button
                      type="button"
                      variant="danger"
                      icon={Trash2}
                      onClick={() => setConfirmOpen(true)}
                      disabled={isSaving}
                    >
                      Delete
                    </Button>
                  ) : null}
                </div>
                <div className={styles.rightActions}>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => navigate('/weighbridge/settings/weighbridges')}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" variant="primary" loading={isSaving}>
                    {isNew ? 'Create Weighbridge' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            ) : null}
          </form>
          ) : null}
        </>
      )}

      <ConfirmModal
        open={confirmOpen}
        title="Delete weighbridge?"
        message={
          <p>
            Delete <strong>{form.name || 'this weighbridge'}</strong>? This cannot be undone.
          </p>
        }
        variant="danger"
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmOpen(false)}
        loading={deleteMut.isPending}
      />
    </div>
  );
}
